/**
 * Hooks for badge category management + badge rarity moderation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { badgeCategoriesApi, badgeModerationApi } from '@api/endpoints/badgeCategories'
import type {
  CreateBadgeCategoryRequest,
  UpdateBadgeCategoryRequest,
  ListCollectionBadgesParams,
  SetBadgeRarityRequest,
} from '@api/types/badgeCategory'

// ===================
// BADGE CATEGORY QUERY HOOKS
// ===================

/**
 * Fetch all badge categories
 */
export function useBadgeCategories(includeInactive: boolean = false) {
  return useQuery({
    queryKey: ['badge-categories', { includeInactive }],
    queryFn: () => badgeCategoriesApi.getCategories({ includeInactive }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch a single badge category
 */
export function useBadgeCategory(id: number | undefined) {
  return useQuery({
    queryKey: ['badge-category', id],
    queryFn: () => badgeCategoriesApi.getCategory(id!),
    enabled: id !== undefined,
  })
}

// ===================
// BADGE CATEGORY MUTATION HOOKS
// ===================

/**
 * Create a new badge category
 */
export function useCreateBadgeCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateBadgeCategoryRequest) =>
      badgeCategoriesApi.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['badge-categories'] })
    },
  })
}

/**
 * Update an existing badge category
 */
export function useUpdateBadgeCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBadgeCategoryRequest }) =>
      badgeCategoriesApi.updateCategory(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['badge-category', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['badge-categories'] })
    },
  })
}

/**
 * Delete a badge category
 */
export function useDeleteBadgeCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => badgeCategoriesApi.deleteCategory(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['badge-category', id] })
      queryClient.invalidateQueries({ queryKey: ['badge-categories'] })
    },
  })
}

// ===================
// RARITY MODERATION HOOKS
// ===================

/**
 * Fetch collection badges for rarity moderation
 */
export function useCollectionBadgesForModeration(params: ListCollectionBadgesParams = {}) {
  return useQuery({
    queryKey: ['collection-badges-moderation', params],
    queryFn: () => badgeModerationApi.getCollectionBadges(params),
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Set a collection badge's rarity
 */
export function useSetBadgeRarity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ badgeId, data }: { badgeId: string; data: SetBadgeRarityRequest }) =>
      badgeModerationApi.setRarity(badgeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-badges-moderation'] })
    },
  })
}
